// @noflow
import { cloneDeep, values } from 'lodash';
import { ROLE_ADMIN, bindToRoleById } from './Roles';

export const initialState = {
  content: {
    profiles: [],
    passcode: {
      requirePasscode: false,
      fetching: false,
      error: false,
    },
  },
  hidden: false,
  mode: '',
  id: '',
  /*
   * Profile that is passed to the ProfileForm component for edit one on web only
   */
  updateProfileOnWeb: {
    profile: null,
  },
  profileAvatars: {
    profileList: [],
    fetching: false,
  },
  download: {
    fetching: false,
    error: false,
  },
  delete: {
    profileId: '',
    fetching: false,
    error: false,
  },
  create: {
    profile: null,
    fetching: false,
    error: false,
  },
  update: {
    profile: null,
    fetching: false,
    error: false,
  },
  isPasscodeSet: {
    fetching: false,
    error: false,
    isPasscodeSet: false,
  },
  isSecurityQuestionSet: {
    fetching: false,
    error: false,
    isSecurityQuestionSet: false,
  },
  parentalControlRatings: {
    getFetching: false,
    getError: false,
    setFetching: false,
    setError: false,
    movieRatings: [],
    showRatings: [],
    passcode: '',
  },
  currentProfile: {
    profile: undefined,
    fetching: false,
    error: false,
    profileId: undefined,
    role: '',
    ageLevel: '',
  },
  profileModalRequired: false,
};

/*
 * Number of profiles with 'Admin' role could be more then one.
 * In this case there is no Master profile (Master means that profile that can be deleted).
 * If number of profile with 'Admin' role is one, then define this profile as Master.
 */
function defineMasterProfile(payload) {
  const profiles = payload.profiles.map(profiles => values(profiles)[0]);
  const admins = profiles.filter(profile => bindToRoleById(profile.role) === ROLE_ADMIN);
  const masterProfile = admins.length === 1 ? admins[0] : null;
  profiles.forEach(profile => {
    profile.isMaster = profile === masterProfile;
  });
  return payload;
}

function checkIsRatingEnabled(ratingsList, ratingSlug, elementRatingName) {
  for (let index = 0; index < ratingsList.length; index += 1) {
    if (ratingsList[index].slug === ratingSlug) {
      return ratingsList[index].filter.indexOf(elementRatingName) > -1;
    }
  }
  return false;
}

export function updateRatingsListWithEnabledStatus(availableRatings, ratingSlug) {
  return availableRatings.reduce((acc, element) => {
    element = { ...element };
    if (element.slug !== 'none') {
      element.isEnabled = checkIsRatingEnabled(
        availableRatings,
        ratingSlug,
        element.name.replace('/', ''),
      );
      acc.push(element);
    }
    return acc;
  }, []);
}

export function updateRatingsListWithNewRating(newRating, ratingList) {
  let rating;
  let newRatingSlug;

  if (newRating.isEnabled) {
    rating = ratingList.find(
      element => element.filter.indexOf(newRating.name.replace('/', '')) === -1,
    );
    newRatingSlug = rating ? rating.slug : 'none';
  } else {
    newRatingSlug = newRating.slug;
  }

  return updateRatingsListWithEnabledStatus(ratingList, newRatingSlug);
}

function updateRatingsFromBlockedList(availableRatings, blockedList) {
  const blockedRatings = (blockedList || '')
    .replace('[', '')
    .replace(']', '')
    .split(/,\s*/);

  return availableRatings
    .filter(rating => rating.slug !== 'none')
    .map(rating => ({
      ...rating,
      isEnabled: blockedRatings.includes(rating.name.replace('/', '')),
    }));
}

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case 'REQUEST_USER_PROFILES':
      return {
        ...state,
        download: {
          fetching: true,
          error: false,
        },
      };
    case 'RECEIVE_USER_PROFILES': {
      const { payload } = action;
      const { profiles, require_passcode: requirePasscode } = defineMasterProfile(payload);

      return {
        ...state,
        content: {
          profiles,
          passcode: {
            requirePasscode,
            fetching: false,
            error: false,
          },
        },
        download: {
          fetching: false,
          error: false,
        },
      };
    }
    case 'REQUEST_USER_PROFILES_ERROR':
      return {
        ...state,
        download: {
          error: true,
          fetching: false,
        },
      };
    case 'REQUEST_DELETE_USER_PROFILE':
      return {
        ...state,
        delete: {
          profileId: action.profileId,
          fetching: true,
          error: false,
        },
      };
    case 'RECEIVE_DELETE_USER_PROFILE':
      return {
        ...state,
        delete: {
          profileId: action.profileId,
          fetching: false,
          error: false,
        },
      };
    case 'REQUEST_DELETE_USER_PROFILE_ERROR':
      return {
        ...state,
        delete: {
          profileId: action.profileId,
          fetching: false,
          error: true,
        },
      };
    case 'CREATE_USER_PROFILE':
      return {
        ...state,
        create: {
          profile: action.profile,
          error: false,
          fetching: true,
        },
      };
    case 'CREATE_USER_PROFILE_SUCCESS':
      return {
        ...state,
        create: {
          profile: action.profile,
          error: false,
          fetching: false,
        },
      };
    case 'CREATE_USER_PROFILE_ERROR':
      return {
        ...state,
        create: {
          profile: action.profile,
          error: true,
          fetching: false,
        },
      };
    case 'UPDATE_USER_PROFILE_SUCCESS':
      return {
        ...state,
        update: {
          profile: action.profile,
          error: false,
          fetching: false,
        },
      };
    case 'UPDATE_USER_PROFILE_ERROR':
      return {
        ...state,
        update: {
          profile: action.profile,
          error: true,
          fetching: false,
        },
      };
    case 'UPDATE_USER_PROFILE':
      return {
        ...state,
        update: {
          profile: action.profile,
          error: false,
          fetching: true,
        },
      };

    case 'REQUEST_USER_PROFILE_IMAGES':
      return {
        ...state,
        profileAvatars: {
          ...state.profileAvatars,
          fetching: true,
        },
      };

    case 'RECEIVE_USER_PROFILE_IMAGES':
      return {
        ...state,
        profileAvatars: {
          profileList: action.payload,
          fetching: false,
        },
      };
    case 'REQUEST_USER_PROFILE_IMAGES_ERROR':
      return {
        ...state,
        profileAvatars: {
          ...state.profileAvatars,
          fetching: false,
        },
      };

    case 'REQUEST_CHECK_PASSCODE_EXIST': {
      const {
        isPasscodeSet: { isPasscodeSet },
      } = state;
      return {
        ...state,
        isPasscodeSet: {
          error: false,
          fetching: true,
          isPasscodeSet,
        },
      };
    }

    case 'RECEIVE_CHECK_PASSCODE_EXIST': {
      const { payload } = action;
      return {
        ...state,
        isPasscodeSet: {
          fetching: false,
          error: false,
          isPasscodeSet: payload ? payload.length > 0 : false,
        },
      };
    }
    case 'REQUEST_CHECK_PASSCODE_EXIST_ERROR':
      return {
        ...state,
        isPasscodeSet: {
          error: true,
          fetching: false,
          isPasscodeSet: false,
        },
      };
    case 'REQUEST_CHECK_SECURITY_QUESTION_EXIST':
      return {
        ...state,
        isSecurityQuestionSet: {
          error: false,
          fetching: true,
          isSecurityQuestionSet: false,
        },
      };
    case 'RECEIVE_CHECK_SECURITY_QUESTION_EXIST': {
      const { payload } = action;
      return {
        ...state,
        isSecurityQuestionSet: {
          fetching: false,
          error: false,
          isSecurityQuestionSet: payload ? payload.length > 0 : false,
        },
      };
    }
    case 'REQUEST_CHECK_SECURITY_QUESTION_EXIST_ERROR':
      return {
        ...state,
        isSecurityQuestionSet: {
          error: true,
          fetching: false,
          isSecurityQuestionSet: false,
        },
      };
    case 'PROFILE_MODE_CHANGE':
      return {
        ...state,
        mode: action.payload,
      };
    case 'SET_PROFILE_FOR_UPADATE_ON_WEB':
      return {
        ...state,
        updateProfileOnWeb: {
          profile: action.payload,
        },
      };
    case 'REQUEST_PARENTAL_CONTROL_RATINGS':
      return {
        ...state,
        parentalControlRatings: {
          ...state.parentalControlRatings,
          getError: false,
          getFetching: true,
        },
      };
    case 'RECEIVE_PARENTAL_CONTROL_RATINGS': {
      const { availableSettings, installedSettings } = action.payload;
      return {
        ...state,
        parentalControlRatings: {
          ...state.parentalControlRatings,
          getError: false,
          getFetching: false,
          movieRatings: updateRatingsFromBlockedList(
            availableSettings.movies,
            installedSettings.parental_controls.movie_ratings_to_block,
          ),
          showRatings: updateRatingsFromBlockedList(
            availableSettings.shows,
            installedSettings.parental_controls.show_ratings_to_block,
          ),
          passcode: installedSettings.parental_controls.passcode,
        },
      };
    }
    case 'REQUEST_PARENTAL_CONTROL_RATINGS_ERROR':
      return {
        ...state,
        parentalControlRatings: {
          ...state.parentalControlRatings,
          getError: true,
          getFetching: false,
        },
      };
    case 'REQUEST_APPLY_PARENTAL_CONTROL_RATINGS':
      return {
        ...state,
        parentalControlRatings: {
          ...state.parentalControlRatings,
          setError: false,
          setFetching: true,
        },
      };
    case 'RECEIVE_APPLY_PARENTAL_CONTROL_RATINGS':
      return {
        ...state,
        parentalControlRatings: {
          ...state.parentalControlRatings,
          setError: false,
          setFetching: false,
        },
      };
    case 'REQUEST_APPLY_PARENTAL_CONTROL_RATINGS_ERROR':
      return {
        ...state,
        parentalControlRatings: {
          ...state.parentalControlRatings,
          setError: true,
          setFetching: false,
        },
      };
    case 'SET_PARENTAL_CONTROL_RATINGS': {
      const { payload } = action;
      const { rating, type } = payload;

      const showRatings =
        type === 'shows'
          ? updateRatingsListWithNewRating(rating, state.parentalControlRatings.showRatings)
          : state.parentalControlRatings.showRatings;

      const movieRatings =
        type === 'movies'
          ? updateRatingsListWithNewRating(rating, state.parentalControlRatings.movieRatings)
          : state.parentalControlRatings.movieRatings;

      return {
        ...state,
        parentalControlRatings: {
          ...state.parentalControlRatings,
          movieRatings,
          showRatings,
        },
      };
    }
    case 'UPDATE_PARENTAL_CONTROL_RATINGS': {
      const { movieRatings, showRatings } = action.payload;
      return {
        ...state,
        parentalControlRatings: {
          ...state.parentalControlRatings,
          movieRatings: cloneDeep(movieRatings),
          showRatings: cloneDeep(showRatings),
        },
      };
    }
    case 'RECEIVE_VERIFY_PASSCODE':
    case 'RECEIVE_UPDATE_PASSCODE': {
      return {
        ...state,
        parentalControlRatings: {
          ...state.parentalControlRatings,
          passcode: action.payload,
        },
      };
    }
    case 'REQUEST_SET_CURRENT_PROFILE_ID': {
      return {
        ...state,
        currentProfile: {
          ...state.currentProfile,
          fetching: true,
          error: false,
        },
      };
    }
    case 'RECEIVE_SET_CURRENT_PROFILE_ID': {
      const {
        payload: { newProfileId, role: roleNew, ageLevel },
      } = action;

      /* TODO: rework stet.content.profiles data structure - it should be just array of profiles
         instead of array of objects with single key */
      const profileHashMap = state.content.profiles.find(
        profile => Object.keys(profile)[0] === newProfileId,
      );
      const profile = profileHashMap ? profileHashMap[Object.keys(profileHashMap)[0]] : undefined;
      const role = roleNew || profile.role;

      return {
        ...state,
        currentProfile: {
          profile,
          fetching: false,
          error: false,
          profileId: newProfileId,
          role,
          ageLevel,
        },
      };
    }
    case 'REQUEST_SET_CURRENT_PROFILE_ID_ERROR':
      return {
        ...state,
        currentProfile: {
          ...state.currentProfile,
          fetching: false,
          error: true,
        },
      };
    case 'REQUEST_SET_PARENTAL_CONTROL_REQUIRE_PASSCODE_PREFERENCE': {
      const {
        payload: { require },
      } = action;
      return {
        ...state,
        content: {
          ...state.content,
          passcode: {
            requirePasscode: require,
            fetching: true,
            error: false,
          },
        },
      };
    }
    case 'RECEIVE_SET_PARENTAL_CONTROL_REQUIRE_PASSCODE_PREFERENCE': {
      const { content } = state;
      return {
        ...state,
        content: {
          ...content,
          passcode: {
            ...content.passcode,
            fetching: false,
            error: false,
          },
        },
      };
    }
    case 'REQUEST_SET_PARENTAL_CONTROL_REQUIRE_PASSCODE_PREFERENCE_ERROR': {
      const { content } = state;
      return {
        ...state,
        content: {
          ...content,
          passcode: {
            ...content.passcode,
            fetching: false,
            error: true,
          },
        },
      };
    }
    case 'SET_DEFAULT_PROFILE_ID':
      return state;
    case 'SET_PROFILE_MODAL_REQUIRED': {
      const { payload } = action;
      return { ...state, profileModalRequired: payload };
    }
    case 'REQUEST_ACTIVE_PROFILE':
      return {
        ...state,
        currentProfile: {
          ...state.currentProfile,
          fetching: true,
          error: false,
        },
      };
    case 'RECEIVE_ACTIVE_PROFILE': {
      const {
        payload: { profile },
      } = action;
      return {
        ...state,
        currentProfile: {
          ...state.currentProfile,
          fetching: false,
          error: false,
          profile,
          profileId: profile.id,
        },
      };
    }
    case 'REQUEST_ACTIVE_PROFILE_ERROR':
      return {
        ...state,
        currentProfile: {
          ...state.currentProfile,
          fetching: false,
          error: true,
        },
      };

    // $FlowExpectedError
    case 'SIGN_OUT':
      return initialState;
    default:
      (action.type: empty); /* eslint-disable-line */
      return state;
  }
};

export default reducer;
